import { useMemo } from 'react';
import type { DataSourceId } from '@shared/data-sources';
import type { DataSourceFreshness } from '@shared/types/data-freshness';
import { getDataFreshness } from '@/lib/data-freshness-api';
import { invalidateLoaders, useLoader } from '@/hooks/use-loader';
import { useAuthStore } from '@/stores/authStore';

/** Reuse an answer for 5 minutes when another page asks; poll every 15 while the tab is visible. */
const STALE_MS = 5 * 60_000;
const POLL_MS = 15 * 60_000;

function dataFreshnessKey(teamId: string): string {
  return `${teamId}:data-freshness`;
}

type FreshnessResult = {
  /** The newest answer that arrived, kept through later failed checks. */
  sources: DataSourceFreshness[] | null;
  /** The latest check's failure, or null. */
  error: string | null;
};

/**
 * The last good answer per team. A failed check returns it with the error, so the shared cache
 * never trades a known "not connected" for nothing (a page would then load the source's data).
 */
const lastGood = new Map<string, DataSourceFreshness[]>();

async function loadFreshness(teamId: string, signal: AbortSignal): Promise<FreshnessResult> {
  const res = await getDataFreshness({ signal });
  if (res.data) {
    lastGood.set(teamId, res.data.sources);
    return { sources: res.data.sources, error: null };
  }
  return { sources: lastGood.get(teamId) ?? null, error: res.error ?? "Couldn't check the data." };
}

export type DataFreshnessState = {
  /** Each source's freshness, once loaded. */
  sources: ReadonlyMap<DataSourceId, DataSourceFreshness> | undefined;
  loading: boolean;
  /** Why the latest check failed (a sentence for people), or null. */
  error: string | null;
};

/**
 * The team's data freshness (GET /api/data-freshness), shared by every chip and page through one
 * cached loader: a page with several chips makes one request. Read by DataFreshness and
 * NotConnectedState; pages use it to choose NotConnectedState over an empty table.
 */
export function useDataFreshness(): DataFreshnessState {
  const teamId = useAuthStore((s) => s.user?.active_team?.id);
  const { data, loading } = useLoader(
    (signal) => (teamId ? loadFreshness(teamId, signal) : Promise.resolve({ sources: null, error: null })),
    [teamId],
    {
      key: teamId ? dataFreshnessKey(teamId) : undefined,
      enabled: Boolean(teamId),
      staleMs: STALE_MS,
      pollMs: POLL_MS,
    }
  );
  const list = data?.sources;
  const sources = useMemo(
    () => (list ? new Map(list.map((entry) => [entry.source, entry])) : undefined),
    [list]
  );
  return { sources, loading, error: data?.error ?? null };
}

/** After a sync or a new connection: chips on screen reload behind their data. */
export function invalidateDataFreshness(teamId: string): void {
  invalidateLoaders(dataFreshnessKey(teamId));
}
